import type { SupabaseClient } from '@supabase/supabase-js'

/** The own `profiles` row as RLS "read own" returns it (0001 + 0002 columns). */
export interface ProfileRow {
  displayName: string
  level: number
  xp: number
  coins: number
  gems: number
  races: number
  wins: number
  finishes: number
  /** The Supabase user is anonymous (`user.is_anonymous`): a guest (O-27). */
  isGuest: boolean
}

/** One row of `my_recent_matches` (0002), newest first. */
export interface RecentMatch {
  endedAt: string
  trackId: string
  placement: number
  /** null = did not finish. */
  finishMs: number | null
  xpEarned: number
  coinsEarned: number
}

/**
 * What the progress UI needs from Supabase (T2.4). `AuthSession.profile` holds one when signed
 * in, null offline; the client behind it was loaded lazily by `initAuth`, so this module only
 * imports types and keeps supabase-js out of the menu bundle.
 */
export interface ProfileApi {
  /** Throws when the read fails; null when the row does not exist. */
  fetchProfile(): Promise<ProfileRow | null>
  recentMatches(n: number): Promise<RecentMatch[]>
  /** `set_display_name` (T2.6): throws `NameRefused` when the name is not allowed. */
  setDisplayName(name: string): Promise<void>
}

/** `set_display_name` refused the name (SQLSTATE 22023): not a picker-shaped name. */
export class NameRefused extends Error {}

type Raw = Record<string, unknown>

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

export function parseProfileRow(raw: unknown, isGuest: boolean): ProfileRow | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Raw
  return {
    displayName: typeof r.display_name === 'string' ? r.display_name : '',
    level: Math.max(1, num(r.level)),
    xp: num(r.xp),
    coins: num(r.coins),
    gems: num(r.gems),
    races: num(r.races),
    wins: num(r.wins),
    finishes: num(r.finishes),
    isGuest,
  }
}

export function parseRecentMatches(raw: unknown): RecentMatch[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((r): r is Raw => typeof r === 'object' && r !== null)
    .map((r) => ({
      endedAt: typeof r.ended_at === 'string' ? r.ended_at : '',
      trackId: typeof r.track_id === 'string' ? r.track_id : '',
      placement: num(r.placement),
      finishMs: typeof r.finish_ms === 'number' ? r.finish_ms : null,
      xpEarned: num(r.xp_earned),
      coinsEarned: num(r.coins_earned),
    }))
}

const COLUMNS = 'display_name, level, xp, coins, gems, races, wins, finishes'
/** The 0001 columns: the read before migration 0002 is applied (counters then show 0). */
const COLUMNS_0001 = 'display_name, level, xp, coins, gems'
/** Postgres undefined column / PostgREST and Postgres missing function. */
const MISSING_COLUMN = '42703'
const MISSING_FUNCTION = new Set(['PGRST202', '42883'])
/** `set_display_name`'s "invalid display name". */
const INVALID_NAME = '22023'

type Query = { data: unknown; error: { code?: string; message: string } | null }

/** The `ProfileApi` over a signed-in Supabase client. */
export function supabaseProfileApi(supabase: SupabaseClient): ProfileApi {
  const select = (userId: string, columns: string): PromiseLike<Query> =>
    supabase.from('profiles').select(columns).eq('id', userId).maybeSingle()
  return {
    async fetchProfile() {
      const { data: auth } = await supabase.auth.getSession()
      const user = auth.session?.user
      if (!user) return null
      let res = await select(user.id, COLUMNS)
      // Production runs ahead of migration 0002 for a while: fall back to the 0001 columns.
      if (res.error?.code === MISSING_COLUMN) res = await select(user.id, COLUMNS_0001)
      if (res.error) throw new Error(res.error.message)
      return parseProfileRow(res.data, user.is_anonymous === true)
    },
    async recentMatches(n) {
      const res = await supabase.rpc('my_recent_matches', { n })
      // Before migration 0002 there is no race history to show.
      if (res.error && MISSING_FUNCTION.has(res.error.code)) return []
      if (res.error) throw new Error(res.error.message)
      return parseRecentMatches(res.data as unknown)
    },
    async setDisplayName(name) {
      const { error } = await supabase.rpc('set_display_name', { new_name: name })
      if (error?.code === INVALID_NAME) throw new NameRefused(error.message)
      if (error) throw new Error(error.message)
    },
  }
}
